import { TrendingUp, Building, DollarSign, Calendar, ArrowRight, Percent } from "lucide-react";
import { useInvestments } from "@/hooks/useInvestments";

const OffPlanInvestments = () => {
  const { data: investments, isLoading } = useInvestments(true);
  const displayInvestments = investments?.slice(0, 3) ?? [];

  const benefits = [
    { icon: DollarSign, title: "Capital Appreciation", description: "Secure long-term growth in Nairobi's prime real estate market" },
    { icon: Percent, title: "Rental Income", description: "Generate consistent passive income from premium tenants" },
    { icon: Calendar, title: "Flexible Payment", description: "Spread payments over construction period with milestone-based plans" },
    { icon: Building, title: "Early Bird Pricing", description: "Lock in below-market rates before completion and price appreciation" },
  ];

  return (
    <section id="off-plan-investments" className="bg-brand-canvas py-16 md:py-24">
      <div className="px-6 md:px-16 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="font-open-sans font-semibold text-xs tracking-[0.3em] uppercase text-accent mb-6">Investment Opportunities</p>
          <h2 className="font-open-sans font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-primary-foreground mb-8">Off-Plan Investments</h2>
          <div className="max-w-3xl mx-auto">
            <p className="font-body text-lg leading-relaxed text-primary-foreground/75">
              Secure your future with our carefully selected off-plan projects in Nairobi's most promising developments.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {benefits.map((benefit) => (
            <div key={benefit.title} className="bg-accent border border-accent/20 rounded-xl p-6 text-center backdrop-blur-sm hover:bg-accent/90 transition-colors">
              <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                <benefit.icon className="w-6 h-6 text-accent-foreground" />
              </div>
              <h4 className="font-display text-lg font-light text-accent-foreground">{benefit.title}</h4>
              <p className="font-body text-xs text-accent-foreground leading-relaxed">{benefit.description}</p>
            </div>
          ))}
        </div>

        {isLoading ? (
          <p className="text-center text-primary-foreground/70 font-body mb-16">Loading investments...</p>
        ) : displayInvestments.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {displayInvestments.map((investment) => (
              <div key={investment.id} className="group overflow-hidden rounded-xl border border-border/30 bg-card/95 transition-all duration-300 hover:bg-card">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={investment.image} alt={investment.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/20 to-transparent" />
                  <div className="absolute top-4 right-4">
                    <div className="px-3 py-1 bg-foreground/90 rounded-full">
                      <span className="font-body text-xs text-primary-foreground font-medium">{investment.completion}</span>
                    </div>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <h3 className="font-display text-xl font-light text-primary-foreground mb-2">{investment.title}</h3>
                    <div className="flex items-center gap-3 text-primary-foreground/80 text-sm mb-2">
                      <span>{investment.location}</span><span>•</span><span>{investment.type}</span>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-2xl font-display font-bold text-accent">{investment.price}</div>
                    <div className="px-3 py-1 bg-accent/90 rounded-full">
                      <span className="font-body text-xs text-accent-foreground font-medium">{investment.roi}</span>
                    </div>
                  </div>
                  <div className="space-y-2 mb-4">
                    {investment.features.slice(0, 3).map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-accent rounded-full" />
                        <span className="font-body text-xs text-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <a href="/off-plan-investments" className="inline-flex items-center gap-2 font-body text-sm text-foreground hover:text-accent transition-colors">
                    View Details <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-primary-foreground/70 font-body mb-16">No investment opportunities available yet.</p>
        )}

        <div className="text-center">
          <a href="/off-plan-investments" className="inline-block font-body text-xs tracking-[0.25em] uppercase bg-accent text-accent-foreground px-10 py-4 hover:bg-foreground hover:text-background transition-colors duration-300">
            Explore Investment Opportunities
          </a>
        </div>
      </div>
    </section>
  );
};

export default OffPlanInvestments;
